import { AddressFile } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { CREATE_ADDRESS_FILE_MUTATION } from 'src/graphql/entities/address-file/address-file.operations';
import { useNotifications } from 'src/utils/hooks';

export interface CreateAddressFileMutation {
  createAddressFile: Omit<AddressFile, 'uploadedByUser' | 'address' | 'partner' | 'content' | 'deletedAt'>[];
}

export type CreateAddressFileMutationParams = {
  data: Pick<
    AddressFile,
    'addressId' | 'contentType' | 'fileKey' | 'fileName' | 'size' | 'partnerId' | 'uploadedBy'
  >;
};

export const useCreateAddressFile = () => {
  const { showErrorAlert } = useNotifications();

  const [createAddressFileMutation, { loading: isCreating }] = useMutation<
    CreateAddressFileMutation,
    CreateAddressFileMutationParams
  >(CREATE_ADDRESS_FILE_MUTATION, {
    optimisticResponse: (params) => ({
      createAddressFile: [
        {
          __typename: 'AddressFile',
          id: new Date().getTime().toString(),
          addressFileId: new Date().getTime().toString(),
          contentType: params.data.contentType,
          fileKey: params.data.fileKey,
          fileName: params.data.fileName,
          pinnedAt: '',
          size: params.data.size,
          addressId: params.data.addressId,
          uploadedBy: params.data.uploadedBy,
          partnerId: params.data.partnerId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deletedAt: '',
        },
      ],
    }),
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to upload address file, please try again');
    },
  });

  const createAddressFile = useCallback(
    async ({
      file,
      key,
      addressId,
      userId,
      partnerId,
    }: {
      file?: File;
      key: string;
      userId?: string;
      addressId?: string;
      partnerId?: number;
    }) => {
      if (!addressId || !userId || !partnerId) {
        throw new Error('Missing required parameters: addressId, userId, or partnerId');
      }
      if (!file) {
        throw new Error('File is required');
      }
      await createAddressFileMutation({
        variables: {
          data: {
            addressId,
            contentType: file.type,
            fileKey: key,
            fileName: file.name,
            size: file.size,
            uploadedBy: userId,
            partnerId,
          },
        },
      });
    },
    [createAddressFileMutation],
  );

  return {
    createAddressFile,
    isCreating,
  };
};
